import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import React from 'react';
import { yupErrorMsg } from '../../../../../components/atoms/const';
import api from '../../../../../components/api';

export interface ContactPersonRes {
  'id': number,
  'firstname': string,
  'lastname': string,
  'position': string,
  'phoneNumber': string,
  'email': string,
  'role': number,
  'comPref': number,
  'hasAccess': string,
  'deleted': number;
  'client': null,
  'partner': null,
}

function useClientsAddContact(formikClient?: any, clientContactId?: number | null) {
  const { clientId, contactId } = useParams();

  // navigation handler
  const navigation = useNavigate();
  const onNavigateToClientDetail = () => navigation(`/clients/${clientId}`);
  const onCancelNavigate = () => navigation(`/clients/${clientId}`);

  const currentContactId = React.useMemo(() => clientContactId ?? contactId, [contactId, clientContactId]);

  // add handler
  const { isLoading: addLoading, mutate: addClientContact } = useMutation(api.clients.addContact, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log('addClient result', res);
      alert('success');
      onNavigateToClientDetail();
    },
    onError: (err: Error) => {
      console.log('err', err.message);
      alert('error');
    },
  });

  const { isLoading: updateLoading, mutate: updateClientContact } = useMutation(api.clients.updateContact, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      console.log('updateClient result', res);
      alert('success');
    },
    onError: (err: Error) => {
      console.log('err', err.message);
      alert('error');
    },
  });

  /* form validation */
  const yupSchema = yup.object().shape({
    firstname: yup.string().required(yupErrorMsg.FIRST_NAME_EMPTY),
    lastname: yup.string().required(yupErrorMsg.LAST_NAME_EMPTY),
    position: yup.string().required(yupErrorMsg.POSITION_EMPTY),
    role: yup.string().required(yupErrorMsg.ROLE_EMPTY),
    comPref: yup.string().required(yupErrorMsg.PREFERENCE_EMPTY),
    email: yup.string()
      .when('phoneNumber', {
        is: (phoneNumber : string) => !phoneNumber || phoneNumber.length === 0,
        then: yup.string()
          .required('Email or Phone number is required'),
      }),
    phoneNumber: yup.string()
      .when('email', {
        is: (email : string) => !email || email.length === 0,
        then: yup.string()
          .required('Email or Phone number is required'),
      }),
  }, [['email', 'phoneNumber']]);

  const currentFormik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      phoneNumber: '',
      position: '',
      role: '',
      comPref: '',
    },
    validationSchema: yupSchema,
    onSubmit: (values) => {
      if (currentContactId && clientId) {
        updateClientContact({ ...values, id: currentContactId.toString() });
      } else {
        addClientContact({
          id: clientId!,
          contactPerson: values,
        });
      }
    },
  });

  const formik = React.useMemo(() => formikClient ?? currentFormik, [currentFormik, formikClient]);

  const { data: rolesRes, isLoading: rolesLoading } = useQuery(['getAllRoles', 'user'], api.user.getAllRoles);

  const roleList = React.useMemo(() => {
    const resData = rolesRes?.data?.data;
    if (resData) {
      return resData.map((role) => ({
        value: role.id,
        label: role.name,
      }));
    }
    return [];
  }, [rolesRes]);

  // Edit Mode! get contact person data from client by id
  const { data: clientByIdRes } = useQuery(['getById', 'clients', clientId], () => api.clients.getById(clientId!), {
    enabled: !!clientId,
  });

  React.useEffect(() => {
    if (clientId && currentContactId && roleList) {
      const resData: ContactPersonRes[] | undefined = clientByIdRes?.data?.data?.contactPerson;
      if (resData) {
        const findResData = resData.find((x) => x.id.toString() === currentContactId.toString());
        if (findResData) {
          formik.setFieldValue('firstname', findResData.firstname ?? '');
          formik.setFieldValue('lastname', findResData.lastname ?? '');
          formik.setFieldValue('email', findResData.email ?? '');
          formik.setFieldValue('phoneNumber', findResData.phoneNumber ?? '');
          formik.setFieldValue('position', findResData.position ?? '');
          formik.setFieldValue('role', findResData.role ?? '');
          formik.setFieldValue('comPref', findResData.comPref ?? '');
        }
      }
    }
  }, [clientByIdRes, roleList]);

  return {
    formik,
    addLoading,
    currentContactId,
    updateLoading,
    roleList,
    rolesLoading,
    onCancelNavigate,
  };
}

export default useClientsAddContact;
